import type { StateCreator } from 'zustand';
import type { Transport, TransportMode } from '@my-fullstack-app/itinerary-types';
import { addMinutes, generateId, sortByStartTime } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import { track } from '../../lib/analytics';
import { nextStartTimeFromAllItems } from './day-item-helpers';

/**
 * Manual-transport surface is intentionally minimal: users add a manual
 * leg and can change a leg's mode; everything else (placement, duration,
 * removal) is owned by the auto-connector engine (`useAutoTransports`),
 * which regenerates legs from the surrounding places.
 */
export interface TransportSlice {
  addTransport: (
    tripId: string,
    dayId: string,
    mode?: TransportMode | undefined,
    durationMinutes?: number | undefined,
    targetStartTime?: string | undefined,
    notes?: string | undefined
  ) => void;
  updateTransportMode: (
    tripId: string,
    dayId: string,
    transportId: string,
    mode: TransportMode
  ) => void;
}

export const createTransportSlice: StateCreator<ItineraryState, [], [], TransportSlice> = (set, get) => ({
  addTransport: (tripId, dayId, mode = 'walk', durationMinutes = 15, targetStartTime, notes) => {
    get()._capture();
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      const day = trip?.days.find((d) => d.id === dayId);
      const startTime = targetStartTime ?? nextStartTimeFromAllItems(day);

      const transport: Transport = {
        id: generateId(),
        mode: mode,
        startTime,
        endTime: addMinutes(startTime, durationMinutes),
        ...(notes ? { notes } : {}),
      };

      return {
        trips: s.trips.map((t) => {
          if (t.id !== tripId) return t;
          return {
            ...t,
            days: t.days.map((d) => {
              if (d.id !== dayId) return d;
              return { ...d, transports: sortByStartTime([...(d.transports ?? []), transport]) };
            }),
          };
        }),
      };
    });
    markTripDirty(tripId, ['days']);
  },

  updateTransportMode: (tripId, dayId, transportId, mode) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          days: t.days.map((d) => {
            if (d.id !== dayId) return d;
            return {
              ...d,
              transports: (d.transports ?? []).map((tr) =>
                tr.id === transportId ? { ...tr, mode } : tr
              ),
            };
          }),
        };
      }),
    }));
    markTripDirty(tripId, ['days']);
    track('transport.mode_changed', { tripId, dayId, mode });
  },
});
